/**
 * What the last "Imputar en Redmine" did: the counts, then one line per problem
 * with a link to its cell. The cells also carry a ✓/⚠ mark, but the text lives
 * here so it can be read in full and by a screen reader.
 */
import styled from 'styled-components'
import type { ImputacionMonth, IsoDate, SubmitResult } from '../../../types/imputacion'
import { ddmm, lineKeyForProject } from './imputacionLogic'

/** Message of a cell whose request got no answer (timeout or network): the client re-read instead. */
export const UNKNOWN_OUTCOME_MESSAGE = 'No sabemos si se guardó: revisa'

export interface SubmitSummary {
    created: number
    updated: number
    deleted: number
    unchanged: number
    stoppedReason: 'auth' | 'uncertain' | null
}

export interface SubmitResultPanelProps {
    month: ImputacionMonth
    results: SubmitResult[]
    summary: SubmitSummary
    onGoToCell: (lineKey: string, iso: IsoDate) => void
    onClose: () => void
}

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: #FAFCFD;
  font-size: ${({ theme }) => theme.fontSizes.sm};
`

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-weight: 700;
`

const Problems = styled.ul`
  margin: 8px 0 0;
  padding-left: 1.1rem;

  li {
    margin: 3px 0;
  }
`

const LinkButton = styled.button`
  margin-left: 6px;
  border: none;
  background: none;
  padding: 0;
  color: #1565C0;
  text-decoration: underline;
  cursor: pointer;
  font-size: inherit;
`

const Close = styled.button`
  border: none;
  background: none;
  font-size: 1.1rem;
  cursor: pointer;
  color: ${({ theme }) => theme.colors.textMuted};
`

interface Problem {
    key: string
    text: string
    lineKey?: string
    iso?: IsoDate
}

function problemsOf(month: ImputacionMonth, results: SubmitResult[]): Problem[] {
    const label = (pid: number) => month.lines.find((l) => l.redmine_project_id === pid)?.label ?? `proyecto ${pid}`
    const out: Problem[] = []
    const conflictSeen = new Set<IsoDate>()
    for (const r of results) {
        const lineKey = lineKeyForProject(month, r.project_id)
        const base = { key: `${r.spent_on}|${r.project_id}`, lineKey, iso: r.spent_on }
        const where = `${ddmm(r.spent_on)} · ${label(r.project_id)}`
        switch (r.status) {
            case 'conflict':
                if (conflictSeen.has(r.spent_on)) break
                conflictSeen.add(r.spent_on)
                out.push({
                    ...base,
                    text: `${ddmm(r.spent_on)} cambió en Redmine mientras editabas; ya ves los datos nuevos. Revisa el día y vuelve a introducir el valor.`,
                })
                break
            case 'unconfirmed':
                out.push({
                    ...base,
                    text:
                        r.message === UNKNOWN_OUTCOME_MESSAGE
                            ? `${where}: ${UNKNOWN_OUTCOME_MESSAGE}`
                            : `No pudimos confirmar la entrada del ${ddmm(r.spent_on)} en «${label(r.project_id)}». Hemos vuelto a leer tus horas: compruébalo en la tabla antes de reenviar.`,
                })
                break
            case 'error': {
                const detail = [r.message, ...(r.errors ?? [])].filter(Boolean).join(' ')
                out.push({ ...base, text: `${where}: ${detail || 'No se pudo imputar.'}` })
                break
            }
            case 'blocked_external':
            case 'updated':
            case 'deleted':
                // updated/deleted only carry a message when part of the change had to stay in Redmine.
                if (r.message) out.push({ ...base, text: `${where}: ${r.message}` })
                break
            default:
                break
        }
    }
    return out
}

export function SubmitResultPanel({ month, results, summary, onGoToCell, onClose }: SubmitResultPanelProps) {
    const problems = problemsOf(month, results)
    const notAttempted = results.filter((r) => r.status === 'not_attempted').length

    return (
        <Panel role="status" aria-live="polite">
            <Head>
                <span>
                    ✓ {summary.created} creadas · {summary.updated} modificadas · {summary.deleted} eliminadas ·{' '}
                    {summary.unchanged} sin cambios
                </span>
                <Close type="button" aria-label="Cerrar resultado" onClick={onClose}>
                    ×
                </Close>
            </Head>
            {(problems.length > 0 || summary.stoppedReason || notAttempted > 0) && (
                <Problems>
                    {summary.stoppedReason === 'auth' && (
                        <li>Redmine rechazó tu contraseña; no se ha imputado nada más. Inicia sesión de nuevo.</li>
                    )}
                    {summary.stoppedReason === 'uncertain' && (
                        <li>Redmine no respondió; hemos parado aquí. Comprueba la tabla antes de reenviar.</li>
                    )}
                    {problems.map((p) => (
                        <li key={p.key}>
                            {p.text}
                            {p.lineKey && p.iso && (
                                <LinkButton type="button" onClick={() => onGoToCell(p.lineKey!, p.iso!)}>
                                    Ir a la celda
                                </LinkButton>
                            )}
                        </li>
                    ))}
                    {notAttempted > 0 && (
                        <li>
                            {notAttempted} {notAttempted === 1 ? 'cambio no se envió' : 'cambios no se enviaron'}: siguen en tu
                            borrador.
                        </li>
                    )}
                </Problems>
            )}
        </Panel>
    )
}
